// src/helpers/sanitizeDashboardData.js

const isUnknownString = (value) => typeof value === "string" && value.toLowerCase() === "unknown";

/**
 * Trim a single Mosaic "info" item down to only what we care about.
 * Shared between employees and customers.
 *
 * Final kept shape:
 * {
 *   mosaic_name,
 *   household_size,
 *   majority_age_range,
 *   bilingual,
 *   married,
 *   with_kids,
 *   without_kids,
 *   home_ownership,
 *   renter,
 *   low_income,
 *   high_income
 * }
 */
const sanitizeMosaicInfoItem = (item) => {
	if (!item || typeof item !== "object") return item;

	const {
		mosaic_name,
		household_size,
		majority_age_range,
		bilingual,
		married,
		with_kids,
		without_kids,
		home_ownership,
		renter,
		low_income,
		high_income,
	} = item;

	return {
		mosaic_name,
		household_size,
		majority_age_range,
		bilingual,
		married,
		with_kids,
		without_kids,
		home_ownership,
		renter,
		low_income,
		high_income,
	};
};

/**
 * Sanitize the mosaic object itself:
 * - If household_id/type/group are "Unknown" → drop mosaic entirely (return undefined)
 * - Else:
 *   - Remove household_id, type, group
 *   - Trim info[] with sanitizeMosaicInfoItem
 */
const sanitizeMosaic = (mosaic) => {
	if (!mosaic || typeof mosaic !== "object") return undefined;

	const { household_id, type, group } = mosaic;

	// Drop completely if it's an "Unknown" mosaic
	if (isUnknownString(household_id) || isUnknownString(type) || isUnknownString(group)) {
		return undefined;
	}

	const { info, household_id: _h, type: _t, group: _g, ...rest } = mosaic;

	let sanitized = { ...rest };

	if (Array.isArray(info)) {
		sanitized.info = info.map(sanitizeMosaicInfoItem);
	}

	return sanitized;
};

/**
 * Sanitize the proposed_location_data_tt map:
 * - For each entry, remove lengthInMeters if it is exactly 0.
 */
const sanitizeProposedLocationTTMap = (map) => {
	if (!map || typeof map !== "object") return map;

	const cleaned = {};

	for (const [key, value] of Object.entries(map)) {
		if (!value || typeof value !== "object") {
			cleaned[key] = value;
			continue;
		}

		const { lengthInMeters, ...rest } = value;

		// Only drop lengthInMeters when it is 0; keep it otherwise
		if (lengthInMeters === 0) {
			cleaned[key] = { ...rest };
		} else {
			cleaned[key] = { lengthInMeters, ...rest };
		}
	}

	return cleaned;
};

const sanitizeProposedLocation = (location) => {
	if (!location || typeof location !== "object") return location;

	// Give me everthing except what's listed below
	const {
		_id,
		pdfs,
		bi_dashboard_id,
		geolocation,
		updated_at,
		created_at,
		employee_short_distance,
		employee_long_distance,
		customer_short_distance,
		customer_long_distance,
		notes,
		status,
		opex_value,
		car_trailer_parking,
		parking_ratio,
		available_sf,
		building_sf,
		office_sf,
		clear_height,
		docs_grade_level_doors,
		base_rent,
		percent_leased,
		electric_price_sf,
		gross_price_sf,
		year_built,
		location_suite,
		leasing_company,
		...rest
	} = location;

	const cleaned = { ...rest };

	// Remove name only if blank / equal to ""
	if (typeof cleaned.name === "string" && cleaned.name.trim() === "") {
		delete cleaned.name;
	}

	return cleaned;
};

const sanitizeEmployee = (employee) => {
	if (!employee || typeof employee !== "object") return employee;

	const { _id, bi_dashboard_id, is_sync, latitude, longitude, geolocation, updated_at, zip_code, mosaic, ...rest } =
		employee;

	const hasProposedLocationData =
		Array.isArray(employee.proposed_location_data) && employee.proposed_location_data.length > 0;

	const hasProposedLocationDataTt =
		employee.proposed_location_data_tt &&
		typeof employee.proposed_location_data_tt === "object" &&
		Object.keys(employee.proposed_location_data_tt).length > 0;

	const sanitized = { ...rest };

	// Remove current_location_data if proposed_location_data is present/populated
	if (hasProposedLocationData) {
		delete sanitized.current_location_data;
	}

	// Remove proposed_location_data if proposed_location_data_tt is present/populated
	if (hasProposedLocationDataTt) {
		delete sanitized.proposed_location_data;
	}

	// Trim proposed_location_data_tt so lengthInMeters is removed when 0
	if (employee.proposed_location_data_tt) {
		sanitized.proposed_location_data_tt = sanitizeProposedLocationTTMap(employee.proposed_location_data_tt);
	}

	// Mosaic trimming
	const sanitizedMosaic = sanitizeMosaic(mosaic);
	if (sanitizedMosaic) {
		sanitized.mosaic = sanitizedMosaic;
	}

	// Remove unreachable_locations if null OR empty array
	if (
		employee.unreachable_locations == null ||
		(Array.isArray(employee.unreachable_locations) && employee.unreachable_locations.length === 0)
	) {
		delete sanitized.unreachable_locations;
	}

	return sanitized;
};

const sanitizeCustomer = (customer) => {
	if (!customer || typeof customer !== "object") return customer;

	const {
		// Remove
		_id,
		bi_dashboard_id,
		is_sync,
		latitude,
		longitude,
		geolocation,
		updated_at,
		mosaic,
		...rest
	} = customer;

	const sanitized = { ...rest };

	const hasProposedLocationDataTt =
		customer.proposed_location_data_tt &&
		typeof customer.proposed_location_data_tt === "object" &&
		Object.keys(customer.proposed_location_data_tt).length > 0;

	// Remove current_location_data if proposed_location_data_tt has data
	if (hasProposedLocationDataTt) {
		delete sanitized.current_location_data;
	}

	// Remove proposed_location_data if proposed_location_data_tt has data
	if (hasProposedLocationDataTt) {
		delete sanitized.proposed_location_data;
	}

	// Trim proposed_location_data_tt so lengthInMeters is removed when 0
	if (customer.proposed_location_data_tt) {
		sanitized.proposed_location_data_tt = sanitizeProposedLocationTTMap(customer.proposed_location_data_tt);
	}

	// Remove unreachable_locations if empty array
	if (Array.isArray(customer.unreachable_locations) && customer.unreachable_locations.length === 0) {
		delete sanitized.unreachable_locations;
	}

	// Mosaic trimming
	const sanitizedMosaic = sanitizeMosaic(mosaic);
	if (sanitizedMosaic) {
		sanitized.mosaic = sanitizedMosaic;
	}

	return sanitized;
};

export const sanitizeDashboardData = (data) => {
	if (!data || typeof data !== "object") return data;

	const {
		commute_type,
		locationToCompare,
		locationToCompareName,
		id,
		dashboard_title,
		max_traveltime_seconds,
		is_industrial_location_type,
		latitude,
		longitude,
		is_process_completed,
		current_location_drivetime_average,
		updated_at,
		rolePermissions,
		scoreInsightsDashboards,
		...rest
	} = data;

	const sanitized = {
		...rest,
	};

	const hasProposedLocations = Array.isArray(data.proposedLocations) && data.proposedLocations.length > 0;

	// If proposedLocations has data, remove these root-level fields:
	// - current_location
	// - notes
	// - status
	// - leasing_company
	if (hasProposedLocations) {
		delete sanitized.current_location;
		delete sanitized.notes;
		delete sanitized.status;
		delete sanitized.leasing_company;
	}

	// Remove root-level gross/opex when 0
	if (sanitized.gross_price_sf === 0) {
		delete sanitized.gross_price_sf;
	}
	if (sanitized.opex_value === 0) {
		delete sanitized.opex_value;
	}

	// Always remove mosaic_cluster_groups
	if ("mosaic_cluster_groups" in sanitized) {
		delete sanitized.mosaic_cluster_groups;
	}

	// Sanitize soc_codes: keep only soc_name
	if (Array.isArray(data.soc_codes)) {
		sanitized.soc_codes = data.soc_codes.map((code) => ({
			soc_name: code.soc_name,
		}));
	}

	// Sanitize proposedLocations
	if (Array.isArray(data.proposedLocations)) {
		sanitized.proposedLocations = data.proposedLocations.map(sanitizeProposedLocation);
	}

	// Sanitize townsonEmployee
	if (Array.isArray(data.townsonEmployee)) {
		sanitized.townsonEmployee = data.townsonEmployee.map(sanitizeEmployee);
	}

	// Sanitize customers
	if (Array.isArray(data.customers)) {
		sanitized.customers = data.customers.map(sanitizeCustomer);
	}

	return sanitized;
};
